import { RefObject, useEffect, useRef, useState } from 'react';

/**
 * Whether an element's text is currently clipped by `text-overflow: ellipsis`, so a tooltip can
 * offer the full value only when part of it is actually hidden.
 *
 * Re-measures on resize, because a table column's width — and therefore whether its label fits —
 * changes with the viewport rather than with the text. `ResizeObserver` is guarded so the hook is
 * safe under jest's `testEnvironment: 'node'`.
 *
 * Pass the text as `dependency` so the measurement is redone when the label itself changes.
 */
export const useIsTruncated = <T extends HTMLElement>(dependency?: unknown): [RefObject<T>, boolean] => {
  const ref = useRef<T>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const check = () => setIsTruncated(element.scrollWidth > element.offsetWidth);
    check();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(check);
    observer.observe(element);

    return () => observer.disconnect();
  }, [dependency]);

  return [ref, isTruncated];
};

export default useIsTruncated;
